import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { FavoriteControl, FavoriteSpace } from '../../../favorite/models/favorite';
import { FavoriteControlsService } from '../../../favorite/services/favorite-controls.service';
import { FavoritePlacesService } from '../../../favorite/services/favorite-places.service';
import { FavoriteSpacesService } from '../../../favorite/services/favorite-spaces.service';
import { UserService } from '../../../user/services/user.service';
import { LocationChange } from '../../../user/types/location-change';
import { DashboardCacheService } from './dashboard-cache.service';

@Injectable({
    providedIn: 'root'
})
export class ViewModelSubscriptionService {

    constructor(
        private dashboardCacheService: DashboardCacheService,
        private favoritePlacesService: FavoritePlacesService,
        private favoriteSpacesService: FavoriteSpacesService,
        private favoriteControlsService: FavoriteControlsService,
        private userService: UserService,
    ) {
    }

    public handleControlFavorite(favorite: FavoriteControl) {
        const location = this.dashboardCacheService.getCachedDashboard(favorite.locationId);
        const device = location.areaDevices.get(favorite.spaceId).find(d => d.hardwareItem.id === favorite.controlId);
        if (!device.subscribedToUpdates && location.startViewModelUpdates && location.startViewModelUpdates.has(device.hardwareItem.id)) {
            location.startViewModelUpdates.get(device.hardwareItem.id).subscribe();
        }
    }

    public handleControlUnfavorite(favorite: FavoriteControl) {
        const location = this.dashboardCacheService.getCachedDashboard(favorite.locationId);
        const device = location.areaDevices.get(favorite.spaceId).find(d => d.hardwareItem.id === favorite.controlId);
        // Only stop the updates subscription if both the area and location are also not favorited.
        const placeFavorited = this.favoritePlacesService.isFavorite(favorite.locationId);
        const spaceFavorited = this.favoriteSpacesService.isFavorite({ locationId: favorite.locationId, spaceId: favorite.spaceId });
        if (
            device.subscribedToUpdates &&
            location.stopViewModelUpdates &&
            location.stopViewModelUpdates.has(device.hardwareItem.id) &&
            !placeFavorited &&
            !spaceFavorited
        ) {
            const loc = this.userService.activeLocation;
            // Ensure the location isn't the most recently visited, as that location should stay subscribed.
            loc.pipe(take(1)).subscribe(activeLocation => {
                if (activeLocation.id !== location.locationId) {
                    location.stopViewModelUpdates.get(device.hardwareItem.id).subscribe();
                }
            });
        }
    }

    public handleSpaceFavorite(favorite: FavoriteSpace) {
        const location = this.dashboardCacheService.getCachedDashboard(favorite.locationId);
        const subscriptions: Observable<void>[] = [];
        location.areaDevices.get(favorite.spaceId).forEach(d => {
            const deviceId = d.hardwareItem.id;
            // Only start updates subscription if the device in the area is not already subscribed.
            if (!d.subscribedToUpdates && location.startViewModelUpdates && location.startViewModelUpdates.has(deviceId)) {
                subscriptions.push(location.startViewModelUpdates.get(deviceId));
            }
        });

        forkJoin(subscriptions).subscribe();
    }

    public handleSpaceUnfavorite(favorite: FavoriteSpace) {
        const location = this.dashboardCacheService.getCachedDashboard(favorite.locationId);
        const subscriptions: Observable<void>[] = [];
        location.areaDevices.get(favorite.spaceId).forEach(d => {
            const deviceId = d.hardwareItem.id;
            // Only stop updates subscription if the device is subscribed and the device and location are not also favorited.
            if (
                d.subscribedToUpdates &&
                location.stopViewModelUpdates &&
                location.stopViewModelUpdates.has(deviceId) &&
                !this.favoritePlacesService.isFavorite(favorite.locationId) &&
                !this.favoriteControlsService.isFavorite({ locationId: favorite.locationId, spaceId: favorite.spaceId, controlId: deviceId })
            ) {
                // Ensure the location isn't the most recently visited, as that location should stay subscribed.
                subscriptions.push(this.userService.activeLocation.pipe(take(1)).pipe(switchMap(activeLocation => {
                    if (activeLocation.id !== location.locationId) {
                        return location.stopViewModelUpdates.get(deviceId);
                    }
                    return new Observable<void>();
                })));
            }
        });

        forkJoin(subscriptions).subscribe();
    }

    public handlePlaceFavorite(favorite: number) {
        const location = this.dashboardCacheService.getCachedDashboard(favorite);
        const subscriptions: Observable<void>[] = [];
        location.areas.forEach(a => {
            location.areaDevices.get(a.areaId).forEach(d => {
                const deviceId = d.hardwareItem.id;
                // Only start updates subscription if the device in the location is not already subscribed.
                if (!d.subscribedToUpdates && location.startViewModelUpdates && location.startViewModelUpdates.has(deviceId)) {
                    subscriptions.push(location.startViewModelUpdates.get(deviceId));
                }
            });
        });

        forkJoin(subscriptions).subscribe();
    }

    public handlePlaceUnfavorite(favorite: number) {
        const location = this.dashboardCacheService.getCachedDashboard(favorite);
        const subscriptions: Observable<void>[] = [];
        location.areas.forEach(a => {
            location.areaDevices.get(a.areaId).forEach(d => {
                const deviceId = d.hardwareItem.id;
                // Only stop updates subscription if the device is subscribed and the device and area are not also favorited.
                if (
                    d.subscribedToUpdates &&
                    location.stopViewModelUpdates &&
                    location.stopViewModelUpdates.has(deviceId) &&
                    !this.favoriteSpacesService.isFavorite({ locationId: favorite, spaceId: a.areaId }) &&
                    !this.favoriteControlsService.isFavorite({ locationId: favorite, spaceId: a.areaId, controlId: deviceId })
                ) {
                    // Ensure the location isn't the most recently visited, as that location should stay subscribed.
                    subscriptions.push(this.userService.activeLocation.pipe(take(1)).pipe(switchMap(activeLocation => {
                        if (activeLocation.id !== location.locationId) {
                            return location.stopViewModelUpdates.get(deviceId);
                        }
                        return new Observable<void>();
                    })));
                }
            });
        });

        forkJoin(subscriptions).subscribe();
    }

    public handleActiveLocationChange(locationChange: LocationChange) {
        const oldLocation = this.dashboardCacheService.getCachedDashboard(locationChange.oldLocationId);
        const newLocation = this.dashboardCacheService.getCachedDashboard(locationChange.newLocationId);
        const subscriptions: Observable<void>[] = [];
        if (oldLocation && oldLocation.areas) {
            oldLocation.areas.forEach(a => {
                oldLocation.areaDevices.get(a.areaId).forEach(d => {
                    const deviceId = d.hardwareItem.id;
                    // Only stop updates subscription if the device is subscribed and the location, area, and device are not favorited.
                    if (
                        d.subscribedToUpdates &&
                        oldLocation.stopViewModelUpdates &&
                        oldLocation.stopViewModelUpdates.has(deviceId) &&
                        !this.favoritePlacesService.isFavorite(locationChange.oldLocationId) &&
                        !this.favoriteSpacesService.isFavorite({ locationId: locationChange.oldLocationId, spaceId: a.areaId }) &&
                        !this.favoriteControlsService.isFavorite({ locationId: locationChange.oldLocationId, spaceId: a.areaId, controlId: deviceId })
                    ) {
                        subscriptions.push(oldLocation.stopViewModelUpdates.get(deviceId));
                    }
                });
            });
        }

        if (newLocation && newLocation.areas) {
            newLocation.areas.forEach(a => {
                newLocation.areaDevices.get(a.areaId).forEach(d => {
                    const deviceId = d.hardwareItem.id;
                    // Only start updates subscription if the device in the location is not already subscribed.
                    if (!d.subscribedToUpdates && newLocation.startViewModelUpdates && newLocation.startViewModelUpdates.has(deviceId)) {
                        subscriptions.push(newLocation.startViewModelUpdates.get(deviceId));
                    }
                });
            });
        }

        forkJoin(subscriptions).subscribe();
    }
}
